import {
  CommunicationSessionEventLogConferenceParticipantJoin,
  CommunicationSessionEventLogDialReservationOffer,
  UserData,
} from '@pwp-common';

import { CommunicationTaskName } from '../../../../../../../../common/src/objects/communication/communication-task';
import { RowCell } from '../../../../common/p-table/row-cell';
import { StatusBadgeColor } from '../../../shared/status-badge/status-badge-color';
import { CommunicationSessionDialedCallLogBase } from '../base/communication-session-dialed-call-log-base';

export class DialReservationOfferRow extends CommunicationSessionDialedCallLogBase<CommunicationSessionEventLogDialReservationOffer> {
  //////////////////////////////////////////////////////////////////////////////////////////
  // Variables
  //////////////////////////////////////////////////////////////////////////////////////////

  public connectedToClient: RowCell<boolean>;

  //////////////////////////////////////////////////////////////////////////////////////////
  // Constructor
  //////////////////////////////////////////////////////////////////////////////////////////
  constructor({
    dialedCall,
    allDataUser,
    timezone,
    conferenceParticipantJoinEvents,
  }: {
    dialedCall: CommunicationSessionEventLogDialReservationOffer;
    allDataUser: Map<string, UserData>;
    timezone: string;
    conferenceParticipantJoinEvents?: CommunicationSessionEventLogConferenceParticipantJoin[];
  }) {
    super({
      dialedCall,
      allDataUser,
      timezone,
      userId: dialedCall.getParticipant().getParticipantId(),
      userStatus: dialedCall.getUserStatus(),
    });
    this.updateOriginationReason(dialedCall);
    this.setConnectedToClient(dialedCall, conferenceParticipantJoinEvents);
    this.setCallSummary(dialedCall, conferenceParticipantJoinEvents);
  }

  //////////////////////////////////////////////////////////////////////////////////////////
  // Origination Reason
  //////////////////////////////////////////////////////////////////////////////////////////
  private updateOriginationReason(dialedCall: CommunicationSessionEventLogDialReservationOffer) {
    const translationKeys = {
      [CommunicationTaskName.callbackRequest]: 'cellOriginationReasonDialCallbackRequest',
      [CommunicationTaskName.conversationRequest]: 'cellOriginationReasonDialConversationRequest',
    };

    const translationKey = translationKeys[dialedCall.getCommunicationTaskType()];

    if (translationKey) {
      this.originationReason = new RowCell<string>({
        translationScope: 'communication-session-event-row',
        translationKey,
        translationObj: {
          ...this.userDisplayNameWithEmail.translationObj,
        },
        sortValue: translationKey,
        styleClass: undefined,
      });
    }
  }

  //////////////////////////////////////////////////////////////////////////////////////////
  // Connected to client
  //////////////////////////////////////////////////////////////////////////////////////////
  private setConnectedToClient(
    dialedCall: CommunicationSessionEventLogDialReservationOffer,
    conferenceParticipantJoinEvents: CommunicationSessionEventLogConferenceParticipantJoin[],
  ) {
    const hasParticipantJoined = DialReservationOfferRow.hasParticipantJoined(
      dialedCall,
      conferenceParticipantJoinEvents,
    );
    this.connectedToClient = new RowCell<boolean>({
      translationScope: 'communication-session-event-row',
      translationKey: hasParticipantJoined ? 'cellConnectedToClientTrue' : 'cellConnectedToClientFalse',
      translationObj: {},
      sortValue: hasParticipantJoined,
      statusBadgeColor: hasParticipantJoined ? StatusBadgeColor.green : undefined,
    });
  }

  //////////////////////////////////////////////////////////////////////////////////////////
  // Summary
  //////////////////////////////////////////////////////////////////////////////////////////
  protected setCallSummary(
    dialedCall: CommunicationSessionEventLogDialReservationOffer,
    conferenceParticipantJoinEvents: CommunicationSessionEventLogConferenceParticipantJoin[],
  ) {
    const translationObj = super.getSummaryTranslationObj(dialedCall);
    if (DialReservationOfferRow.hasParticipantJoined(dialedCall, conferenceParticipantJoinEvents)) {
      this.summary = new RowCell<string>({
        translationScope: 'communication-session-event-row',
        translationKey: 'cellSummaryConnectedToClient',
        translationObj,
        sortValue: 'cellSummaryConnectedToClient',
        statusBadgeColor: StatusBadgeColor.green,
      });
      return;
    }
    super.setSummary(dialedCall, dialedCall.getUserStatus());
  }

  //////////////////////////////////////////////////////////////////////////////////////////
  // Has Participant Joined
  //////////////////////////////////////////////////////////////////////////////////////////

  private static hasParticipantJoined(
    dialedCall: CommunicationSessionEventLogDialReservationOffer,
    conferenceParticipantJoinEvents: CommunicationSessionEventLogConferenceParticipantJoin[],
  ) {
    return (
      conferenceParticipantJoinEvents?.some(
        (event) => event.getCallSid() === dialedCall.getCallSid() && event.getSequenceNumber() > 1,
      ) ?? false
    );
  }
}
